'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    category: string;
    manufacturer: string;
    image: string;
    alt: string;
    compliance: string[];
    inStock: boolean;
  };
  onQuickView: (id: string) => void;
}

export default function ProductCard({ product, onQuickView }: ProductCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-brand-lg transition-all duration-300 group">
      <div className="relative h-64 bg-muted overflow-hidden">
        <AppImage
          src={product.image}
          alt={product.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {!product.inStock && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="bg-error text-error-foreground px-4 py-2 rounded-md font-semibold">
              Out of Stock
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => onQuickView(product.id)}
            className="p-2 bg-white text-text-primary hover:bg-primary hover:text-primary-foreground rounded-full transition-all duration-250"
            aria-label="Quick view"
          >
            <Icon name="EyeIcon" size={20} />
          </button>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <p className="text-xs text-text-secondary uppercase tracking-wide mb-1">
              {product.category}
            </p>
            <h3 className="text-lg font-semibold text-text-primary line-clamp-2 mb-1">
              {product.name}
            </h3>
            <p className="text-sm text-text-secondary">{product.manufacturer}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {product.compliance.map((cert) => (
            <span
              key={cert}
              className="inline-flex items-center gap-1 px-2 py-1 bg-success bg-opacity-10 text-success text-xs font-medium rounded"
            >
              <Icon name="CheckBadgeIcon" size={14} variant="solid" />
              {cert}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}